import { Stack, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { Tooltip } from 'src/components';
import { CustomViewAttribute, UIType } from 'src/graphql/entities/custom-view';
import { extractDisabledUserTextFromName } from 'src/graphql/entities/platform-user';

import {
  AvatarCell,
  BooleanCell,
  ChipCell,
  CountBubbleCell,
  DateCell,
  IconCell,
  SinceTimeCell,
  TextCell,
} from 'src/pages/CustomViews/custom-views-entities-table';
import { useOverflowObserver } from 'src/utils/hooks';

interface UITypePropsMap {
  [UIType.BubbleNumber]: number;
  [UIType.SinceTime]: string;
  [UIType.Date]: string;
  [UIType.Text]: string;
  [UIType.Boolean]: string;
  [UIType.List]: string;
  [UIType.Textarea]: string;
  [UIType.Number]: string;
  [UIType.Icon]: string;
  [UIType.Avatar]: string;
  [UIType.Custom]: string;
  [UIType.Chip]: string;
  [UIType.Chips]: string[];
}

type GeneralComponentFunction<T extends UIType> = (
  value: UITypePropsMap[T],
  attribute: CustomViewAttribute,
) => ReactNode;

export const generalComponentsMap: {
  [K in UIType]: GeneralComponentFunction<K> | null;
} = {
  [UIType.Custom]: null,

  [UIType.BubbleNumber]: (value: number) => <CountBubbleCell count={value} />,
  [UIType.SinceTime]: (value: string) => <SinceTimeCell date={value} />,
  [UIType.Date]: (value: string, attribute: CustomViewAttribute) => (
    <DateCell
      date={value}
      dateFormat={attribute.dateFormat}
      includeTime={attribute.includeTime}
    />
  ),
  [UIType.Text]: (value: string, attribute: CustomViewAttribute) => {
    const theme = useTheme();
    return (
      <TextCell
        textColor={attribute?.getTextColor?.(value, theme)}
        text={attribute?.formatValue?.(value) ?? value}
      />
    );
  },
  [UIType.Boolean]: (value: string) => <BooleanCell value={value} />,
  [UIType.List]: (value: string) => <TextCell text={value} />,
  [UIType.Textarea]: (value: string) => <TextCell text={value} />,
  [UIType.Number]: (value: string) => <TextCell text={value} />,
  [UIType.Icon]: (icon: string) => <IconCell icon={icon} />,
  [UIType.Avatar]: (name: string, attribute: CustomViewAttribute) => (
    <AvatarCell
      subTitle={attribute?.getSubTitleText?.(name) ?? ''}
      fullName={extractDisabledUserTextFromName(name)}
    />
  ),
  [UIType.Chip]: (value: string, attribute: CustomViewAttribute) => {
    const theme = useTheme();
    return (
      <ChipCell
        label={attribute.formatValue?.(value) ?? value}
        textColor={attribute.getTextColor?.(value, theme)}
        backgroundColor={attribute.getBackgroundColor?.(value, theme)}
      />
    );
  },
  [UIType.Chips]: (values: string[], attribute: CustomViewAttribute) => {
    const theme = useTheme();
    const { nodeRef, isOverflowing } = useOverflowObserver();
    // Guard against non-array values to prevent runtime errors
    if (!Array.isArray(values) || !values.length) return null;
    const chips = values?.map((value) => {
      const renderValue = attribute?.renderValue?.(value) ?? value;
      return (
        <ChipCell
          key={renderValue}
          label={renderValue}
          textColor={attribute.getTextColor?.(value, theme)}
          backgroundColor={attribute.getBackgroundColor?.(value, theme)}
        />
      );
    });
    return (
      <Tooltip
        title={
          isOverflowing ? (
            <Stack useFlexGap gap={1} direction="row" flexWrap="wrap">
              {chips}
            </Stack>
          ) : null
        }
        placement="bottom-start"
        slotProps={{
          tooltip: {
            sx: {
              padding: theme.spacing(1),
            },
          },
        }}
      >
        <Stack useFlexGap gap={1} direction="row" ref={nodeRef}>
          {chips}
        </Stack>
      </Tooltip>
    );
  },
};
